import React, { useState, useEffect, useMemo, useRef } from "react";
import { FaFilter } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import * as XLSX from "xlsx";
import axiosInstance from "../helpers/axiosInstance";
import Swal from "sweetalert2";

const Vehicle = () => {
  const [vehicles, setVehicles] = useState([]);
  const [allVehicles, setAllVehicles] = useState([]); // Raw data for export and options
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [inputPage, setInputPage] = useState(1);

  const [filters, setFilters] = useState({
    vendorCode: "",
    vehicleId: "",
    entryTime: "",
    search: "",
  });

  const [activeFilter, setActiveFilter] = useState("");
  const [tooltip, setTooltip] = useState({ visible: false, content: '', left: 0, top: 0 });

  const cache = useRef({});

  // Helper function to format yyyy-mm-dd to dd-mm-yyyy for display
  const formatDate = (yyyymmdd) => {
    if (!yyyymmdd || yyyymmdd === "-") return "-";
    const [year, month, day] = yyyymmdd.split("-");
    return `${day}-${month}-${year}`;
  };

  const getPunctualityClass = (punct) => {
    switch (punct?.toLowerCase()) {
      case 'late':
        return 'text-danger';
      case 'early':
        return 'text-warning';
      case 'on time':
        return 'text-success';
      default:
        return '';
    }
  };

  const filteredVehicles = useMemo(() => {
    return allVehicles.filter((v) => {
      const searchMatch = !filters.search || [
        v.truck_number,
        v.route_name,
        v.vendor_code,
        v.status,
        v.punctuality,
        v.entry_time,
        v.unloading_time,
      ].some((field) =>
        field && field.toString().toLowerCase().includes(filters.search.toLowerCase())
      );

      const vendorMatch = !filters.vendorCode || v.vendor_code === filters.vendorCode;
      const vehicleMatch = !filters.vehicleId || v.truck_number === filters.vehicleId;

      const entryTimeMatch = !filters.entryTime || (v.entry_time && v.entry_time >= filters.entryTime);

      return (
        searchMatch &&
        vendorMatch &&
        vehicleMatch &&
        entryTimeMatch
      );
    });
  }, [allVehicles, filters]);

  const limit = 8;
  const paginatedVehicles = useMemo(() => {
    const startIndex = (pagination.page - 1) * limit;
    return filteredVehicles.slice(startIndex, startIndex + limit);
  }, [filteredVehicles, pagination.page, limit]);

  const toggleFilter = (filterName) => {
    setActiveFilter((prev) => (prev === filterName ? "" : filterName));
  };

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("api/vehicle/vehiclestatuslogs"); // Adjust endpoint as needed
      const data = res.data.data || [];
      setAllVehicles(data);
      setVehicles(data);
    } catch (err) {
      console.error(err);
      setAllVehicles([]);
      setVehicles([]);
    }
    setLoading(false);
  };

  const handleRouteHover = async (route, e) => {
    if (!route) return;

    if (cache.current[route]) {
      setTooltip({
        visible: true,
        content: `Bay Time From: ${cache.current[route].bay_time_from || '-'}\nBay Time To: ${cache.current[route].bay_time_to || '-'}`,
        left: e.clientX + 10,
        top: e.clientY + 10
      });
      return;
    }

    try {
      const res = await axiosInstance.get(`/api/trip/getbays/${encodeURIComponent(route)}`);
      const data = res.data.data || {};
      cache.current[route] = data;
      setTooltip({
        visible: true,
        content: `Bay Time From: ${data.bay_time_from || '-'}\nBay Time To: ${data.bay_time_to || '-'}`,
        left: e.clientX + 10,
        top: e.clientY + 10
      });
    } catch (err) {
      console.error(err);
      setTooltip({
        visible: true,
        content: 'Error fetching bay times',
        left: e.clientX + 10,
        top: e.clientY + 10
      });
    }
  };

  const handleRouteLeave = () => {
    setTooltip({ visible: false });
  };

  const handleExport = () => {
    try {
      const exportData = filteredVehicles.map((item, index) => ({
        "Sl. No.": index + 1,
        "Vendor Code": item.vendor_code || "-",
        "Vehicle": item.truck_number || "-",
        "Route Name": item.route_name || "-",
        "Entry Time": item.entry_time || "-",
        "Unloading Time": item.unloading_time || "-",
        "Status": item.status || "-",
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Vehicle Status Logs");

      // Show SweetAlert popup **before** file download starts
      Swal.fire({
        icon: "success",
        title: "Export Started",
        text: "File Downloading has Started!",
        confirmButtonText: "OK",
      });

      // Trigger file download
      XLSX.writeFile(wb, "vehicle-status-logs.xlsx");
    } catch (error) {
      console.error("Error exporting data:", error);
      Swal.fire("Error!", "Failed to export file. Please try again.", "error");
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, []);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredVehicles.length / limit));
    const current = Math.min(pagination.page, totalPages);
    setPagination((prev) => ({ ...prev, page: current, totalPages }));
    setInputPage(current);
  }, [filteredVehicles, limit]);


  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
      setInputPage(newPage);
    }
  };

  const handleInputChange = (e) => {
    setInputPage(e.target.value);
  };

  const handleInputSubmit = () => {
    const newPage = Number(inputPage);
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    } else {
      setInputPage(pagination.page); // reset if invalid
    }
  };


  const clearFilters = () => {
    setFilters({
      vendorCode: "",
      vehicleId: "",
      entryTime: "",
      search: "",
    });
    setActiveFilter("");
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Options for dropdowns
  const vendorOptions = useMemo(
    () => [...new Set(allVehicles.map((v) => v.vendor_code).filter(Boolean))],
    [allVehicles]
  );

  const vehicleOptions = useMemo(
    () => [...new Set(allVehicles.map((v) => v.truck_number).filter(Boolean))],
    [allVehicles]
  );

  const currentPage = filteredVehicles.length > 0 ? pagination.page : 0;
  const totalPages = pagination.totalPages || 1;

  if (loading) {
    return <div className="container mt-4">Loading...</div>;
  }

  return (
    <div className="container mt-4">
      <div className="bg-white px-2 py-4 rounded-lg shadow-md">
        {/* Clear Filters & Export Buttons */}
        <div className="d-flex justify-content-end mb-3">
          <button className="btn btn-success mx-2" onClick={handleExport}>
            <i className="bi bi-upload me-1"></i> Export
          </button>
          <button className="btn btn-outline-danger btn-sm" onClick={clearFilters}>
            <i className="fa fa-times me-1" /> Clear All Filters
          </button>
        </div>

        {/* Table */}
        <div className="table-responsive" style={{ overflowY: "unset", maxHeight: "unset" }}>
          <table className="table table-sm">
            <thead className="text-white" style={{ position: "sticky", top: 0, zIndex: 10 }}>
              <tr>
                <th
                  className="text-center py-2 px-0"
                  style={{ backgroundColor: "#0A4595", minWidth: "80px", color: "white" }}
                >
                  Sl. No.
                </th>
                <th
                  className="text-center py-2 px-0 position-relative"
                  style={{ backgroundColor: "#0A4595", minWidth: "150px", color: "white" }}
                >
                  Vendor
                  <FaFilter className="ms-2 cursor-pointer" onClick={() => toggleFilter("vendorCode")} />
                  <br />
                  {activeFilter === "vendorCode" && (
                    <div
                      className="dropdown-menu show p-2 shadow rounded"
                      style={{
                        backgroundColor: "#f8f9fa",
                        zIndex: 20,
                        minWidth: "150px",
                        maxHeight: "200px",
                        overflowY: "auto",
                        position: "fixed"
                      }}
                    >
                      {vendorOptions.map((code) => (
                        <div
                          key={code}
                          className="dropdown-item rounded px-3 py-2 text-dark fw-semibold"
                          onClick={() => {
                            handleFilterChange("vendorCode", code);
                            setActiveFilter("");
                          }}
                          style={{ cursor: "pointer", transition: "background-color 0.2s" }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e9ecef")}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        >
                          {code}
                        </div>
                      ))}
                    </div>
                  )}
                  {filters.vendorCode && (
                    <div
                      className="bg-light d-inline-flex align-items-center px-1 py-0 rounded"
                      style={{ fontSize: "12px", marginLeft: "5px", color: "black" }}
                    >
                      {filters.vendorCode}
                      <IoClose
                        className="text-danger cursor-pointer ms-1"
                        style={{ fontSize: "14px" }}
                        onClick={() => handleFilterChange("vendorCode", "")}
                      />
                    </div>
                  )}
                </th>
                <th
                  className="text-center py-2 px-0 position-relative"
                  style={{ backgroundColor: "#0A4595", minWidth: "150px", color: "white" }}
                >
                  Vehicle
                  <FaFilter className="ms-2 cursor-pointer" onClick={() => toggleFilter("vehicleId")} />
                  <br />
                  {activeFilter === "vehicleId" && (
                    <div
                      className="dropdown-menu show p-2 shadow rounded"
                      style={{
                        backgroundColor: "#f8f9fa",
                        zIndex: 20,
                        minWidth: "150px",
                        maxHeight: "200px",
                        overflowY: "auto",
                        position: "fixed"
                      }}
                    >
                      {vehicleOptions.map((vehicle) => (
                        <div
                          key={vehicle}
                          className="dropdown-item rounded px-3 py-2 text-dark fw-semibold"
                          onClick={() => {
                            handleFilterChange("vehicleId", vehicle);
                            setActiveFilter("");
                          }}
                          style={{ cursor: "pointer", transition: "background-color 0.2s" }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e9ecef")}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        >
                          {vehicle}
                        </div>
                      ))}
                    </div>
                  )}
                  {filters.vehicleId && (
                    <div
                      className="bg-light d-inline-flex align-items-center px-1 py-0 rounded"
                      style={{ fontSize: "12px", marginLeft: "5px", color: "black" }}
                    >
                      {filters.vehicleId}
                      <IoClose
                        className="text-danger cursor-pointer ms-1"
                        style={{ fontSize: "14px" }}
                        onClick={() => handleFilterChange("vehicleId", "")}
                      />
                    </div>
                  )}
                </th>
                <th
                  className="text-center py-2 px-0"
                  style={{ backgroundColor: "#0A4595", minWidth: "150px", color: "white" }}
                >
                  Route
                </th>
                <th
                  className="text-center py-2 px-0 position-relative"
                  style={{ backgroundColor: "#0A4595", minWidth: "120px", color: "white" }}
                >
                  Entry Time
                  <FaFilter className="ms-2 cursor-pointer" onClick={() => toggleFilter("entryTime")} />
                  <br />
                  {activeFilter === "entryTime" && (
                    <div
                      className="dropdown-menu show p-2 shadow rounded"
                      style={{
                        backgroundColor: "#f8f9fa",
                        zIndex: 20,
                        minWidth: "200px",
                        position: "fixed"
                      }}
                    >
                      <input
                        type="time"
                        className="form-control"
                        value={filters.entryTime || ""}
                        onChange={(e) => handleFilterChange("entryTime", e.target.value)}
                        onBlur={() => setActiveFilter("")}
                      />
                    </div>
                  )}
                  {filters.entryTime && (
                    <div
                      className="bg-light d-inline-flex align-items-center px-1 py-0 rounded"
                      style={{ fontSize: "12px", marginLeft: "5px", color: "black" }}
                    >
                      {filters.entryTime}
                      <IoClose
                        className="text-danger cursor-pointer ms-1"
                        style={{ fontSize: "14px" }}
                        onClick={() => handleFilterChange("entryTime", "")}
                      />
                    </div>
                  )}
                </th>
                <th className="text-center py-2 px-0" style={{ backgroundColor: "#0A4595", minWidth: "120px", color: "white" }}>
                  Unloading Time
                </th>
                <th className="text-center py-2 px-0" style={{ backgroundColor: "#0A4595", minWidth: "120px", color: "white" }}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedVehicles.length > 0 ? (
                paginatedVehicles.map((row, idx) => (
                  <tr key={idx}>
                    <td className="text-center p-2">{(pagination.page - 1) * limit + idx + 1}</td>
                    <td className="text-center p-2">{row.vendor_code || "-"}</td>
                    <td className="text-center p-2">{row.truck_number || "-"}</td>
                    <td
                      className="text-center p-2"
                      onMouseEnter={(e) => handleRouteHover(row.route_name, e)}
                      onMouseLeave={handleRouteLeave}
                      style={{ cursor: 'pointer' }}
                    >
                      {row.route_name || "-"}
                    </td>
                    <td className={`text-center p-2 ${getPunctualityClass(row.punctuality)}`}>
                      {row.entry_time || <span className="text-warning">Not Entered</span>}
                    </td>
                    <td className="text-center p-2">{row.unloading_time || <span className="text-warning">Not Unloaded</span>}</td>
                    <td className="text-center p-2">{row.status || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center p-3">
                    No Vehicle Data Available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Tooltip */}
        {tooltip.visible && (
          <div
            style={{
              position: 'fixed',
              left: tooltip.left,
              top: tooltip.top,
              backgroundColor: 'white',
              border: '1px solid #ccc',
              padding: '10px',
              borderRadius: '5px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              zIndex: 1000,
              whiteSpace: 'pre-line',
              fontSize: '14px'
            }}
          >
            {tooltip.content}
          </div>
        )}

        {/* Pagination */}
        <div className="d-flex justify-content-center align-items-center mt-4">
          <button
            className="btn btn-sm mx-2"
            onClick={() => handlePageChange(pagination.page - 1)}
            style={{ backgroundColor: "rgb(10, 69, 149)", color: "white" }}
            disabled={pagination.page === 1 || filteredVehicles.length === 0}
          >
            Previous
          </button>

          <div className="d-flex align-items-center mx-2">
            <span className="mx-1">Page</span>
            <input
              type="number"
              value={inputPage}
              onChange={handleInputChange}
              onBlur={handleInputSubmit}
              onKeyDown={(e) => e.key === "Enter" && handleInputSubmit()}
              style={{ width: "60px", textAlign: "center" }}
              min={1}
              max={pagination.totalPages}
              disabled={filteredVehicles.length === 0}
            />
            <span className="mx-1">of {pagination.totalPages}</span>
          </div>

          <button
            className="btn btn-sm mx-2"
            onClick={() => handlePageChange(pagination.page + 1)}
            style={{ backgroundColor: "rgb(10, 69, 149)", color: "white" }}
            disabled={pagination.page === pagination.totalPages || filteredVehicles.length === 0}
          >
            Next
          </button>
        </div>

      </div>
    </div>
  );
};

export default Vehicle;